<template>
	<div :class="classes" v-if="message !== '' && !hidden" @click="clicked">
		<span class="message" v-html="messageProcessed" />

		<span class="close" v-if="hasClose" @click="close">X</span>
	</div>
</template>

<script>
export default {
	name: "TopMessage",
	// Note: Dynamic emit events from this.click and this.meta.click - may need refactoring for full Vue 3 compatibility
	emits: ["close", "visibility-change"], // Plus dynamic events from component props
	props: {
		message: {
			type: String,
			default: "",
		},
		messageNoClick: {
			type: String,
			default: "",
		},
		colour: {
			type: String,
			default: "red",
		},
		meta: {
			type: Object,
			default: function () {
				return {};
			},
		},
		click: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			hide: false,
			countdownText: "",
			messageProcessed: this.message,
			metaClickNavAllowed: false,
			dismissalReady: false,
			dismissalCheckToken: 0,
		};
	},
	computed: {
		persistentDismissalEnabled() {
			return !!this.meta.close;
		},
		isVisible() {
			return this.message !== "" && !this.hidden;
		},
		hidden() {
			return this.hide || (this.persistentDismissalEnabled && !this.dismissalReady);
		},

		classes() {
			let c = {
				"top-message": true,
				click: this.click !== "" || !!this.metaClickNavAllowed || !!this.meta.click,
			};

			c[this.colour] = true;

			return c;
		},
		hasClose() {
			return !!this.meta.close;
		},
	},
	methods: {
		getDismissalStorageKey(hash) {
			return `partnerup:top-message-dismissed:${hash}`;
		},

		async hashMessageSignature(signature) {
			const encoder = new TextEncoder();
			const data = encoder.encode(signature);

			if (window.crypto?.subtle?.digest) {
				const digest = await window.crypto.subtle.digest("SHA-256", data);
				return Array.from(new Uint8Array(digest))
					.map((b) => b.toString(16).padStart(2, "0"))
					.join("");
			}

			let hash = 2166136261;
			for (let i = 0; i < signature.length; i += 1) {
				hash ^= signature.charCodeAt(i);
				hash = Math.imul(hash, 16777619);
			}

			return `fallback-${(hash >>> 0).toString(16)}`;
		},

		async getMessageHash() {
			const signature = JSON.stringify({
				message: this.message || "",
				messageNoClick: this.meta?.messageNoClick || this.messageNoClick || "",
				colour: this.colour || "",
				countdown: this.meta?.countdown || "",
				clickNav: this.meta?.clickNav || "",
			});

			return this.hashMessageSignature(signature);
		},

		readStoredDismissal(hash) {
			try {
				const raw = window.localStorage.getItem(this.getDismissalStorageKey(hash));
				if (!raw) return null;

				const parsed = JSON.parse(raw);
				if (!parsed?.hash || !parsed?.expiresAt) return null;
				if (Date.now() >= parsed.expiresAt) {
					window.localStorage.removeItem(this.getDismissalStorageKey(hash));
					return null;
				}

				return parsed;
			} catch (err) {
				window.localStorage.removeItem(this.getDismissalStorageKey(hash));
				return null;
			}
		},

		async persistDismissal() {
			const hash = await this.getMessageHash();
			const expiresAt = Date.now() + 3 * 24 * 60 * 60 * 1000;

			window.localStorage.setItem(
				this.getDismissalStorageKey(hash),
				JSON.stringify({
					hash,
					expiresAt,
				})
			);
		},

		async syncDismissalState() {
			const token = this.dismissalCheckToken + 1;
			this.dismissalCheckToken = token;

			if (!this.persistentDismissalEnabled || !this.message) {
				this.hide = false;
				this.dismissalReady = true;
				return;
			}

			this.dismissalReady = false;

			const currentHash = await this.getMessageHash();

			if (this.dismissalCheckToken !== token) return;

			const storedDismissal = this.readStoredDismissal(currentHash);
			this.hide = !!storedDismissal && storedDismissal.hash === currentHash;
			this.dismissalReady = true;
		},

		trim(str, ch) {
			var start = 0,
				end = str.length;

			while (start < end && str[start] === ch) ++start;

			while (end > start && str[end - 1] === ch) --end;

			return start > 0 || end < str.length ? str.substring(start, end) : str;
		},

		async close(e) {
			e.stopPropagation();
			this.hide = true;

			if (this.persistentDismissalEnabled) {
				try {
					await this.persistDismissal();
				} catch (err) {
					console.error("Unable to persist top message dismissal", err);
				}
			}

			this.$emit("close");
		},

		clicked(e) {
			e.stopPropagation();

			if (this.click !== "") {
				this.$emit(this.click);
			}

			if (this.meta.click) {
				this.$emit(this.meta.click);
			}

			if (this.metaClickNavAllowed) {
				this.$router.push({ path: this.meta.clickNav });
			}
		},
		doCountdown() {
			let noCountdown = false;

			this.metaClickNavAllowed = this.meta.clickNav && this.trim(this.meta.clickNav, "/") !== this.trim(window.location.pathname, "/");

			const theMessage = this.meta.clickNav && !this.metaClickNavAllowed && this.meta.messageNoClick ? this.meta.messageNoClick : this.message;

			if (this.meta.countdown) {
				const n = new Date().getTime(),
					n2 = new Date(this.meta.countdown).getTime();

				if (n - n2 > 800) return;

				noCountdown = true;

				if (n > n2) {
					location.reload();
				} else {
					const distance = n2 - n,
						days = Math.floor(distance / (1000 * 60 * 60 * 24)),
						hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
						minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
						seconds = Math.floor((distance % (1000 * 60)) / 1000);

					if (theMessage.indexOf("||countdown||") > 0) {
						this.messageProcessed = theMessage.replace("||countdown||", days + "d " + hours + "h " + minutes + "m " + seconds + "s");

						setTimeout(
							function () {
								this.doCountdown();
							}.bind(this),
							500
						);
					}
				}
			} else {
				this.messageProcessed = theMessage;
			}

			if (!noCountdown) {
				this.metaClickNavAllowed = this.meta.clickNav && this.trim(this.meta.clickNav, "/") !== this.trim(window.location.pathname, "/");
			}
		},
	},
	watch: {
		isVisible: {
			immediate: true,
			handler(value) {
				this.$emit("visibility-change", value);
			},
		},
		message() {
			this.messageProcessed =
				this.meta.clickNav && !this.metaClickNavAllowed && this.meta.messageNoClick ? this.meta.messageNoClick : this.message;
			this.doCountdown();
			this.syncDismissalState();
		},
		colour() {
			this.syncDismissalState();
		},
		meta: {
			deep: true,
			handler() {
				this.messageProcessed =
					this.meta.clickNav && !this.metaClickNavAllowed && this.meta.messageNoClick ? this.meta.messageNoClick : this.message;
				this.doCountdown();
				this.syncDismissalState();
			},
		},
	},
	created() {
		this.messageProcessed = this.meta.clickNav && !this.metaClickNavAllowed && this.meta.messageNoClick ? this.meta.messageNoClick : this.message;

		this.doCountdown();
	},
	mounted() {
		this.syncDismissalState();
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_helper.less";
@import "./../../assets/css/less/_settings.less";

.top-message {
	width: 100%;
	background-color: @red;
	padding: 7px 40px;
	color: #fff;
	text-align: center;
	position: relative;

	&.orange {
		background-color: @orange;
	}

	&.green {
		background-color: @green;
	}

	&.default {
		background-color: @formDefaultColour;
	}

	&.click {
		cursor: pointer;
	}

	.message {
		color: inherit;
	}

	a {
		color: #fff;

		&:hover {
			text-decoration: none;
		}
	}

	.close {
		position: absolute;
		.trans;
		z-index: 3;
		display: block;
		top: 50%;
		margin-top: -12px;
		right: 5px;
		width: 24px;
		height: 24px;
		background: url("../../assets/img/close2.svg") center center no-repeat;
		background-size: 100% auto;
		cursor: pointer;
		.indent;
		transition: transform 0.5s ease-in-out;

		&:hover {
			transform: rotate(180deg);
		}
	}
}
@media screen and (min-width: 768px) {
	.top-message {
		position: fixed;
		top: 64px;
		z-index: 5;
		width: calc(100% - 66px);
		.message {
			white-space: nowrap;
			text-overflow: ellipsis;
			display: block;
			overflow: hidden;
		}
		&:hover {
			.message {
				white-space: inherit;
				text-overflow: inherit;
			}
		}
	}
	.fullMenu {
		.top-message {
			width: calc(100% - 170px);
		}
	}
}
</style>